import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import Navigation from '@/components/Navigation';
import Testimonials from '@/components/Testimonials';
import FloatingActionButtons from '@/components/FloatingActionButtons';
import { useEffect, useRef, useState } from 'react';

const ProjectBox = ({ project, index }: { project: any, index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [visible, setVisible] = useState(false);

  // Handle image loading separately
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Start loading the image
    const img = new Image();
    img.src = project.src;
    
    const onLoad = () => {
      // Schedule state update in the next frame
      requestAnimationFrame(() => {
        setImageLoaded(true);
      });
    };
    
    img.addEventListener('load', onLoad);
    
    // If image is already cached
    if (img.complete) {
      onLoad();
    }

    return () => {
      img.removeEventListener('load', onLoad);
    };
  }, [project.src]);

  // Intersection observer for entry animation without framer-motion
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "0px 0px -50px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative aspect-square overflow-hidden rounded-lg bg-gray-100 hover:shadow-md transition-all duration-300 ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
      style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: 'translate3d(0, 0, 0)',
        willChange: 'transform, opacity',
        transitionProperty: 'transform, opacity, box-shadow',
        transitionDuration: '300ms',
        transitionTimingFunction: 'cubic-bezier(0.33, 0, 0.2, 1)',
        transitionDelay: visible ? `${index * 50}ms` : '0ms',
      }}
    >
      {imageLoaded && (
        <img
          src={project.src}
          alt={project.alt}
          className="w-full h-full object-cover transition-transform duration-500 opacity-100 group-hover:scale-110"
          style={{
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateZ(0) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateZ(0) scale(1)';
          }}
          loading="lazy"
        />
      )}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
    </div>
  );
};

const Referenser = () => {
  const projects = [
    {
      id: "project-hem-1",
      src: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
      alt: "Städare som torkar ett bord i ett hem",
    },
    {
      id: "project-kontor-1",
      src: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
      alt: "Rent kontor med ljusa skrivbord och stolar",
    },
    {
      id: "project-flytt-1",
      src: "https://images.unsplash.com/photo-1457694587812-e8bf29a43845?auto=format&fit=crop&w=1200&q=80",
      alt: "Kartonger vid en flyttstädning",
    },
    {
      id: "project-fonster-1",
      src: "https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=1200&q=80",
      alt: "Fönsterputs med utsikt över staden",
    },
    {
      id: "project-hem-2",
      src: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1200&q=80",
      alt: "Städning av kök med sprayflaska och duk",
    },
    {
      id: "project-kontor-2",
      src: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80",
      alt: "Polerat konferensrum med glasväggar",
    },
    {
      id: "project-trappor-1",
      src: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
      alt: "Nystädad trappa i en byggnad",
    },
    {
      id: "project-hem-3",
      src: "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1200&q=80",
      alt: "Sovrum efter städning med bäddad säng",
    },
  ];

  return (
    <>
      <Navigation />
      <Helmet>
        <title>Galleri - RenGlans Städtjänster</title>
        <meta name="description" content="Lär känna RenGlans - ditt pålitliga städföretag med över 25 års erfarenhet av professionell städning för hem och företag." />
        <meta property="og:title" content="Galleri - RenGlans Städtjänster" />
        <meta property="og:description" content="Lär känna RenGlans - ditt pålitliga städföretag med över 25 års erfarenhet av professionell städning." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-32 pb-20 flex items-center justify-center overflow-hidden" style={{ minHeight: 'calc(80vh - 120px)' }}>
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/house-cleaners.webp)',
            filter: 'blur(4px)',
            transform: 'scale(1.02)',
            zIndex: 0,
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-[hsl(var(--hero-overlay))]/40" />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold mb-4 sm:mb-6 px-2 sm:px-0 leading-tight"
          >
            Galleri
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-6 sm:mb-8 px-2 sm:px-0"
          >
            Med passion för renhet och service sedan 1995
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden translate-y-px">
          <svg 
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none"
            className="w-full h-12 md:h-16"
            
          >
            <path d="M0,60 C200,120 400,0 600,60 C800,120 1000,0 1200,60 V120 H0 V60 Z" fill="#FEF2F2" />
          </svg>
        </div>
      </section>

      {/* Simple Gallery Section */}
      <section className="py-16 bg-[#FEF2F2]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Vårt Arbete</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Se exempel på våra senaste städuppdrag
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {projects.map((project, index) => (
              <ProjectBox key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Red Wave Divider */}
      <div className="relative">
        <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ height: '80px', zIndex: 10, transform: 'translateY(1px)' }}>
          <svg 
            viewBox="0 0 1200 80" 
            preserveAspectRatio="none"
            className="w-full h-full block"
          >
            <path d="M0,0C0,0,100,60,300,40S600,20,900,40s300,20,300,20v20H0V0z" className="fill-red-500" />
          </svg>
        </div>
      </div>

      {/* CTA Section */}
      <section className="relative py-20 bg-red-500 text-white overflow-hidden">
        {/* Subtle box shadow to prevent subpixel rendering issues */}
        <div 
          className="absolute top-0 left-0 right-0 h-[1px] bg-red-500" 
          style={{
            transform: 'translateY(-0.5px)',
            boxShadow: '0 0 0 100vmax #ef4444',
            clipPath: 'inset(0 0 -100vmax)'
          }}
        />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Redo att uppleva RenGlans skillnad?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Boka din kostnadsfria offert idag och låt oss ta hand om dina städbehov.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/'}
            >
              Få Gratis Offert
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/kontakt'}
            >
              Kontakta Oss
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActionButtons />
    </>
  );
};

export default Referenser;
